import { useTranslation } from 'react-i18next';
import { Columns3, Download, Edit3, Loader2, Plus, RefreshCw, RotateCcw, Rows3, Rows4, Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { cn } from '~/lib/utils';
import type { ProductChip } from '~/hooks/use-products-list';
import { PRODUCT_COLUMN_KEYS, type ColumnVisibility, type ProductColumnKey } from '~/hooks/use-column-visibility';

export type Density = 'comfortable' | 'compact';

export interface ChipCounts {
	outOfStock?: number;
	hidden?: number;
	newReleases?: number;
	featured?: number;
	noImage?: number;
}

const CHIP_ORDER: { key: ProductChip; labelKey: string; countKey: keyof ChipCounts }[] = [
	{ key: 'outOfStock', labelKey: 'products.chips.outOfStock', countKey: 'outOfStock' },
	{ key: 'hidden', labelKey: 'products.chips.hidden', countKey: 'hidden' },
	{ key: 'newReleases', labelKey: 'products.chips.newReleases', countKey: 'newReleases' },
	{ key: 'featured', labelKey: 'products.chips.featured', countKey: 'featured' },
	{ key: 'noImage', labelKey: 'products.chips.noImage', countKey: 'noImage' }
];

interface GridChromeProps {
	search: string;
	onSearch: (value: string) => void;
	activeChip: ProductChip | null;
	onChipToggle: (chip: ProductChip) => void;
	counts: ChipCounts;
	activeFilterCount: number;
	onOpenFilters: () => void;
	onNew: () => void;
	onExport: () => void;
	onMassEdit: () => void;
	onMassErpSync: () => void;
	massErpSyncPending: boolean;
	visibleColumns: ColumnVisibility;
	onToggleColumn: (key: ProductColumnKey) => void;
	onResetColumns: () => void;
	selectedCount: number;
	pageIndex: number;
	onClearSelection: () => void;
	rangeFrom: number;
	rangeTo: number;
	total: number;
	density: Density;
	onDensityChange: (density: Density) => void;
	onRefresh: () => void;
}

export function GridChrome({
	search,
	onSearch,
	activeChip,
	onChipToggle,
	counts,
	activeFilterCount,
	onOpenFilters,
	onNew,
	onExport,
	onMassEdit,
	onMassErpSync,
	massErpSyncPending,
	visibleColumns,
	onToggleColumn,
	onResetColumns,
	selectedCount,
	pageIndex,
	onClearSelection,
	rangeFrom,
	rangeTo,
	total,
	density,
	onDensityChange,
	onRefresh
}: GridChromeProps) {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-3">
			{/* Page head — no in-page breadcrumb: the topbar already renders the menu-derived trail */}
			<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
				<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('products.surface.title')}</h1>
				<div className="flex items-center gap-2">
					{/* STUB: Excel export. Legacy admin builds the .xlsx client-side via DevExtreme
					    `exportDataGrid` + `exceljs` + `file-saver` (see Core/client/admin helpers/excelExport.js).
					    admin-v2 has none of those deps and the "no new deps" constraint forbids adding them;
					    there is no server endpoint that returns an .xlsx (the `isExportRequest` loadOption only
					    tells getAllGrid to bypass the page cap/enrichment, it does not produce a file). Kept
					    disabled until a server-side export endpoint or an approved client lib lands. */}
					<Button variant="outline" onClick={onExport} disabled title={t('products.actions.excelExportStubHint')}>
						<Download size={16} /> {t('products.actions.excelExport')}
					</Button>
					<Button onClick={onNew}>
						<Plus size={16} /> {t('products.actions.newProduct')}
					</Button>
				</div>
			</div>

			{/* Toolbar — saved-filter chips share the card with search/actions (one row on wide screens,
			    natural flex-wrap on narrow) instead of a separate stacked bar */}
			<div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
				<span className="mr-1 text-[11px] font-medium uppercase tracking-[0.04em] text-foreground-subtle">{t('products.chips.label')}</span>
				{CHIP_ORDER.map(chip => {
					const active = activeChip === chip.key;
					const count = counts[chip.countKey];
					return (
						<button
							key={chip.key}
							type="button"
							onClick={() => onChipToggle(chip.key)}
							className={cn(
								'inline-flex items-center gap-1.5 rounded-pill border px-3 py-1 text-xs font-medium transition-colors',
								active
									? 'border-brand bg-brand-subtle text-brand-subtle-fg'
									: 'border-border bg-surface text-foreground hover:border-border-strong hover:bg-muted'
							)}>
							{t(chip.labelKey)}
							{count !== undefined && (
								<span className={cn('font-medium', active ? 'text-brand-subtle-fg' : 'text-foreground-subtle')}>· {count}</span>
							)}
						</button>
					);
				})}
				<button
					type="button"
					disabled
					title={t('products.chips.saveCurrentHint')}
					className="inline-flex items-center gap-1 rounded-pill border border-dashed border-border-strong px-3 py-1 text-xs font-medium text-foreground-muted opacity-60">
					<Plus size={12} /> {t('products.chips.saveCurrent')}
				</button>
				<span className="mx-1 h-6 w-px bg-border" aria-hidden />
				<div className="relative min-w-60 flex-1 md:max-w-md">
					<Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-subtle" />
					<Input
						value={search}
						onChange={e => onSearch(e.target.value)}
						placeholder={t('products.toolbar.searchPlaceholder')}
						className="pl-8"
					/>
				</div>
				<Button variant="outline" onClick={onOpenFilters}>
					<SlidersHorizontal size={16} /> {t('common.filters')}
					{activeFilterCount > 0 && (
						<span className="ml-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-pill bg-brand px-1.5 text-[11px] font-semibold text-on-brand">
							{activeFilterCount}
						</span>
					)}
				</Button>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline">
							<Columns3 size={16} /> {t('products.toolbar.columns')}
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-56">
						<DropdownMenuLabel>{t('products.columns.title')}</DropdownMenuLabel>
						<DropdownMenuSeparator />
						{PRODUCT_COLUMN_KEYS.map(key => (
							<DropdownMenuCheckboxItem
								key={key}
								checked={visibleColumns[key]}
								// Keep the menu open while toggling multiple columns.
								onSelect={e => e.preventDefault()}
								onCheckedChange={() => onToggleColumn(key)}>
								{t(`products.grid.columns.${key}`)}
							</DropdownMenuCheckboxItem>
						))}
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={onResetColumns}>
							<RotateCcw size={14} /> {t('products.columns.reset')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<span className="mx-1 h-6 w-px bg-border" aria-hidden />
				<Button variant="outline" onClick={onMassEdit} disabled={selectedCount === 0}>
					<Edit3 size={16} /> {t('products.toolbar.massEdit')}
				</Button>
				<Button variant="outline" onClick={onMassErpSync} disabled={selectedCount === 0 || massErpSyncPending}>
					{massErpSyncPending ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}{' '}
					{t('products.toolbar.massErpSync')}
				</Button>
			</div>

			{/* Selection banner */}
			{selectedCount > 0 && (
				<div className="flex items-center gap-3 rounded-md border border-brand-border-subtle bg-brand-subtle px-3 py-2 text-sm">
					<span className="font-semibold text-brand-subtle-fg">{t('products.selection.count', { count: selectedCount })}</span>
					<span className="text-xs text-foreground-muted">{t('products.selection.onPage', { page: pageIndex + 1 })}</span>
					<span className="ml-auto text-xs text-foreground-muted">{t('products.selection.hint')}</span>
					<button
						type="button"
						onClick={onClearSelection}
						className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs font-medium text-brand-subtle-fg hover:bg-brand/10">
						<X size={12} /> {t('products.selection.clear')}
					</button>
				</div>
			)}

			{/* Grid bar */}
			<div className="flex items-center justify-between gap-4 rounded-t-md border border-b-0 border-border bg-elevated px-3 py-2 text-xs text-foreground-muted">
				<span>
					{t('products.gridBar.showing')}{' '}
					<strong className="font-medium tabular-nums text-foreground">
						{rangeFrom}–{rangeTo}
					</strong>{' '}
					{t('products.gridBar.of')} <strong className="font-medium tabular-nums text-foreground">{total.toLocaleString('el-GR')}</strong>
				</span>
				<div className="flex items-center gap-2">
					<div className="inline-flex rounded-sm border border-border bg-surface p-0.5">
						<button
							type="button"
							title={t('products.gridBar.comfortable')}
							onClick={() => onDensityChange('comfortable')}
							className={cn(
								'rounded-[3px] px-1.5 py-1',
								density === 'comfortable' ? 'bg-muted text-foreground' : 'text-foreground-subtle'
							)}>
							<Rows3 size={13} />
						</button>
						<button
							type="button"
							title={t('products.gridBar.compact')}
							onClick={() => onDensityChange('compact')}
							className={cn('rounded-[3px] px-1.5 py-1', density === 'compact' ? 'bg-muted text-foreground' : 'text-foreground-subtle')}>
							<Rows4 size={13} />
						</button>
					</div>
					<button
						type="button"
						onClick={onRefresh}
						title={t('common.refresh')}
						className="rounded-sm p-1.5 text-foreground-muted hover:bg-muted hover:text-foreground">
						<RefreshCw size={13} />
					</button>
				</div>
			</div>
		</div>
	);
}
